import { useEffect, useState } from 'react'
import { getNow, subscribe } from '../lib/clock'
import { useLatestRef } from './useLatestRef'

/**
 * A value derived from the (overridable) clock that re-renders only when the
 * derived value changes. The preview sim notifies 5x/sec, and most clock-derived
 * UI (a phase, a day, who's checked in where) changes a few times a day (#289).
 * `isEqual` must hold for two calls at the same time with the same inputs, or
 * the render-time resync below loops.
 */
export function useClockSelector<T>(
  select: (now: Date) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
  pollMs = 60_000,
): T {
  const [value, setValue] = useState(() => select(getNow()))
  // Inputs to `select` (event dates, sets, peer data) change between ticks;
  // resync during render so a new input never paints one frame of a stale value.
  const fresh = select(getNow())
  const current = isEqual(value, fresh) ? value : fresh
  if (current !== value) setValue(current)

  const selectRef = useLatestRef(select)
  const isEqualRef = useLatestRef(isEqual)
  useEffect(() => {
    const tick = () =>
      setValue(prev => {
        const next = selectRef.current(getNow())
        return isEqualRef.current(prev, next) ? prev : next
      })
    const id = setInterval(tick, pollMs)
    const unsub = subscribe(tick)
    return () => {
      clearInterval(id)
      unsub()
    }
  }, [pollMs, selectRef, isEqualRef])
  return current
}
